import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { formSnapshotDirty } from "@/lib/formDirty";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

export type HubContactDirectorySource = "hub" | "freshsales" | "freshdesk" | "both" | "staff";

export interface HubContactRow {
  id: number;
  email: string;
  displayName: string | null;
  source: "hub";
  freshdeskContactId: number | null;
  fsContactId: number | null;
  createdAt: string;
  updatedAt: string;
  deactivatedAt: string | null;
}

export interface UnifiedContactSuggestion {
  email: string;
  displayName: string;
  source: HubContactDirectorySource;
  hubContactId: number | null;
  freshdeskContactId: number | null;
  fsContactId: number | null;
  hubUserId: string | null;
}

const CONTACTS_QUERY_KEY = ["user-admin", "contacts"] as const;

function sourceBadgeLabel(source: HubContactDirectorySource | "hub"): string {
  if (source === "staff") {
    return "SGS staff";
  }
  if (source === "hub") {
    return "Hub";
  }
  if (source === "freshsales") {
    return "Freshsales";
  }
  if (source === "freshdesk") {
    return "Freshdesk";
  }
  return "Freshsales & Freshdesk";
}

function sourceBadgeClass(source: HubContactDirectorySource | "hub"): string {
  if (source === "staff") {
    return "bg-indigo-50 text-indigo-800";
  }
  if (source === "hub") {
    return "bg-slate-100 text-slate-700";
  }
  if (source === "freshsales") {
    return "bg-emerald-50 text-emerald-800";
  }
  if (source === "freshdesk") {
    return "bg-sky-50 text-sky-800";
  }
  return "bg-violet-50 text-violet-800";
}

interface ContactDraft {
  email: string;
  displayName: string;
  active: boolean;
}

function emptyDraft(): ContactDraft {
  return { email: "", displayName: "", active: true };
}

function ContactsDirectoryTable({
  contacts,
  canEdit,
  editingId,
  deletePending,
  onEdit,
  onDelete,
  loading,
  error,
}: {
  contacts: HubContactRow[];
  canEdit: boolean;
  editingId: number | "new" | null;
  deletePending: boolean;
  onEdit: (contact: HubContactRow) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
}): JSX.Element {
  const columns = useMemo((): HubDataTableColumn<HubContactRow>[] => {
    return [
      {
        id: "name",
        header: "Name",
        sortValue: (row) => row.displayName ?? row.email,
        cell: (row) => row.displayName ?? row.email,
      },
      {
        id: "email",
        header: "Email",
        sortValue: (row) => row.email,
        cell: (row) => row.email,
      },
      {
        id: "status",
        header: "Status",
        sortValue: (row) => (row.deactivatedAt == null ? "Active" : "Inactive"),
        cell: (row) => (row.deactivatedAt == null ? "Active" : "Inactive"),
      },
      {
        id: "source",
        header: "Source",
        sortValue: () => "Hub",
        cell: () => (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${sourceBadgeClass("hub")}`}>
            {sourceBadgeLabel("hub")}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        sortValue: () => "",
        cell: (row) => (
          <ReadOnlyHoverLayer
            active={!canEdit}
            hoverHint="Admin module administrators can edit hub contacts."
            layout="inline"
          >
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!canEdit || editingId != null}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(row);
                }}
              >
                Edit
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={!canEdit || deletePending}
                isLoading={deletePending}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(row.id);
                }}
              >
                Delete
              </Button>
            </div>
          </ReadOnlyHoverLayer>
        ),
      },
    ];
  }, [canEdit, deletePending, editingId, onDelete, onEdit]);

  return (
    <HubDataTable
      title="Hub contacts"
      columns={columns}
      rows={contacts}
      rowKey={(row) => row.id}
      loading={loading}
      loadingMessage="Loading contacts…"
      error={error}
      empty="No hub contacts yet."
    />
  );
}

export function ContactsPage() {
  const queryClient = useQueryClient();
  const canEdit = useHubModuleAdmin("admin");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(emptyDraft);
  const [draftBaseline, setDraftBaseline] = useState<ContactDraft>(emptyDraft);
  const [formError, setFormError] = useState<string | null>(null);

  const contactsQuery = useQuery({
    queryKey: [...CONTACTS_QUERY_KEY, search],
    queryFn: () =>
      apiFetch<{ contacts: HubContactRow[] }>(
        `/api/user-admin/contacts${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ""}`,
      ),
  });

  const suggestionsQuery = useQuery({
    queryKey: ["user-admin", "contacts", "search", search],
    queryFn: () =>
      apiFetch<{ suggestions: UnifiedContactSuggestion[] }>(
        `/api/hub/contacts/suggestions?q=${encodeURIComponent(search.trim())}`,
      ),
    enabled: search.trim().length >= 2,
  });

  const saveMutation = useMutation({
    mutationFn: (input: { id?: number; body: { email: string; displayName: string | null; active: boolean } }) => {
      if (input.id == null) {
        return apiFetch<{ contact: HubContactRow }>("/api/user-admin/contacts", {
          method: "POST",
          body: input.body,
        });
      }
      return apiFetch<{ contact: HubContactRow }>(`/api/user-admin/contacts/${String(input.id)}`, {
        method: "PATCH",
        body: input.body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
      setEditingId(null);
      setDraft(emptyDraft());
      setFormError(null);
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : "Could not save contact.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: true }>(`/api/user-admin/contacts/${String(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
      if (editingId !== "new" && typeof editingId === "number") {
        setEditingId(null);
        setDraft(emptyDraft());
      }
    },
  });

  const hubContacts = contactsQuery.data?.contacts ?? [];
  const externalSuggestions = useMemo(() => {
    const suggestions = suggestionsQuery.data?.suggestions ?? [];
    const hubEmails = new Set(hubContacts.map((contact) => contact.email.toLowerCase()));
    return suggestions.filter(
      (suggestion) => suggestion.source !== "hub" && !hubEmails.has(suggestion.email.toLowerCase()),
    );
  }, [hubContacts, suggestionsQuery.data?.suggestions]);

  function startCreate(): void {
    const next = emptyDraft();
    setEditingId("new");
    setDraft(next);
    setDraftBaseline(next);
    setFormError(null);
  }

  function startEdit(contact: HubContactRow): void {
    const next = {
      email: contact.email,
      displayName: contact.displayName ?? "",
      active: contact.deactivatedAt == null,
    };
    setEditingId(contact.id);
    setDraft(next);
    setDraftBaseline(next);
    setFormError(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setDraft(emptyDraft());
    setDraftBaseline(emptyDraft());
    setFormError(null);
  }

  function submitDraft(): void {
    const email = draft.email.trim();
    const displayName = draft.displayName.trim();
    if (email.length === 0) {
      setFormError("Email is required.");
      return;
    }
    saveMutation.mutate({
      id: editingId === "new" ? undefined : editingId ?? undefined,
      body: {
        email,
        displayName: displayName.length > 0 ? displayName : null,
        active: draft.active,
      },
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Org-wide contact directory. Hub contacts are editable here; Freshsales and Freshdesk matches appear in search for reference.">
          Admin · Contacts
        </PageTitleWithHelp>
        <ReadOnlyHoverLayer
          active={!canEdit}
          hoverHint="Admin module administrators can add or edit hub contacts."
          layout="inline"
        >
          <Button type="button" variant="primary" disabled={!canEdit || editingId != null} onClick={startCreate}>
            Add hub contact
          </Button>
        </ReadOnlyHoverLayer>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
        </CardHeader>
        <CardBody>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search contacts"
          />
          {search.trim().length >= 2 && externalSuggestions.length > 0 ? (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">External matches</p>
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {externalSuggestions.slice(0, 8).map((suggestion) => (
                  <li key={suggestion.email} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-900">{suggestion.displayName}</div>
                      <div className="text-slate-600">{suggestion.email}</div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${sourceBadgeClass(suggestion.source)}`}
                    >
                      {sourceBadgeLabel(suggestion.source)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {editingId != null ? (
        <Card>
          <CardHeader>
            <CardTitle>{editingId === "new" ? "New hub contact" : "Edit hub contact"}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1 text-sm">
                <span className="font-medium text-slate-700">Email</span>
                <Input
                  value={draft.email}
                  onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
                  disabled={!canEdit || saveMutation.isPending}
                />
              </label>
              <label className="block space-y-1 text-sm">
                <span className="font-medium text-slate-700">Display name</span>
                <Input
                  value={draft.displayName}
                  onChange={(event) => setDraft((current) => ({ ...current, displayName: event.target.value }))}
                  disabled={!canEdit || saveMutation.isPending}
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.active}
                disabled={!canEdit || saveMutation.isPending}
                onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))}
              />
              <span>Active</span>
            </label>
            {formError ? (
              <p className="text-sm text-rose-700" role="alert">
                {formError}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <SaveChangesButton
                type="button"
                variant="primary"
                isDirty={formSnapshotDirty(draft, draftBaseline)}
                disabled={!canEdit || saveMutation.isPending}
                isLoading={saveMutation.isPending}
                onClick={submitDraft}
              >
                Save
              </SaveChangesButton>
              <Button type="button" variant="secondary" disabled={saveMutation.isPending} onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <ContactsDirectoryTable
        contacts={hubContacts}
        canEdit={canEdit}
        editingId={editingId}
        deletePending={deleteMutation.isPending}
        onEdit={startEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        loading={contactsQuery.isLoading}
        error={
          contactsQuery.isError
            ? contactsQuery.error instanceof ApiError
              ? contactsQuery.error.message
              : "Could not load contacts."
            : null
        }
      />
    </div>
  );
}
